"use client";

import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import {
  useSession,
  useSupabaseClient,
  useUser,
} from "../components/SupabaseProvider";
import { PublicationModel } from "../types/models";
import { SocialPostPayload } from "../types/publications";
import { Database } from "../types/supabase";
import { camelToSnakeCase, snakeToCamelCase } from "../utils/api";
import { useDemoMode } from "./demoModeContext";
import { SocialProviderAccount } from "./useSocialProviderAccounts";

// Shared query key factory
const createPublicationsQueryKey = (projectId?: string) =>
  projectId ? ["publications", projectId] : ["publications"];

// Shared fetch function
export const createFetchPublications = (
  supabaseClient: any,
  user: any,
  projectId?: string
) => {
  return async (): Promise<PublicationModel[]> => {
    if (!user) throw new Error("User not found");

    const publications: PublicationModel[] = [];
    let cursor: string | undefined;
    // Load all calendar history and occupied slots. An immutable page key
    // prevents rescheduling from moving rows between pages.
    for (;;) {
      let query = supabaseClient.from("publications").select("*")
        .eq("owner_id", user.id).order("id", { ascending: true }).limit(500);
      if (projectId) query = query.eq("project_id", projectId);
      if (cursor) query = query.gt("id", cursor);
      const { data, error } = await query;
      if (error) throw error;
      if (!data.length) break;
      publications.push(...data.map(deserializePublication));
      cursor = data[data.length - 1].id;
    }
    return publications.sort((a, b) => b.scheduledAt.getTime() - a.scheduledAt.getTime() || a.id.localeCompare(b.id));
  };
};

// Hook for reading publications
export const useReadPublications = (
  projectId?: string,
  options?: Partial<UseQueryOptions<PublicationModel[], Error>>
) => {
  const supabaseClient = useSupabaseClient();
  const user = useUser();
  const queryKey = createPublicationsQueryKey(projectId);
  const fetchPublications = createFetchPublications(
    supabaseClient,
    user,
    projectId
  );
  // Demo mode replaces the calendar's posts (all of them, not a project's).
  const demo = useDemoMode();
  const demoPublications = projectId ? undefined : demo?.publications;

  const query = useQuery({
    queryKey,
    queryFn: fetchPublications,
    enabled: !!user && !demoPublications,
    refetchInterval: (query) => {
      const publications = query.state.data;

      if (!publications) return false;

      const hasPublishingPublications = publications.some(
        (pub) => pub.status === "publishing" || pub.status === "verifying"
      );

      if (hasPublishingPublications) return 5000; // 5s

      const hasScheduledPublications = publications.some(
        (pub) => pub.status === "scheduled"
      );

      if (hasScheduledPublications) return 60000; // 1min

      return false;
    },
    ...options,
  });
  return demoPublications ?? query;
};

// Hook for creating publications
export const useCreatePublication = () => {
  const session = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      account: SocialProviderAccount;
      payload: SocialPostPayload;
      projectId: string;
      scheduledAt?: Date;
    }) => {
      const { account, payload, projectId, scheduledAt } = params;

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/integrations/providers/${account.provider}/accounts/${account.id}/publications`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            project_id: projectId,
            post_info: camelToSnakeCase(payload),
            scheduled_at: scheduledAt?.toISOString() ?? undefined,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();

        if (response.status === 402) {
          const error = new Error("Upgrade Plan") as Error & {
            errorCode?: string;
            requiredPlan?: string;
          };
          error.errorCode = errorData.error_code;
          error.requiredPlan = errorData.required_plan;
          throw error;
        }

        if (response.status === 409) {
          throw new Error(
            "This short has already been posted to this account. To post again, please delete the existing publication first."
          );
        }

        throw new Error(errorData.message || "Failed to post");
      }

      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["publications"],
        exact: false,
      });
    },
  });
};

export type PublishCapacityVerificationResult =
  | { ok: true }
  | {
      ok: false;
      reason: "day_full" | "user_limit" | "account_limit";
      suggestedAt?: Date;
    };

// Advisory pre-flight check for one provider's publish capacity: YouTube's
// daily slots, or the posts a platform takes from one account in 24 hours
// (TikTok, Instagram, Facebook, LinkedIn). Returns one result per requested
// time, in request order. Undefined dates mean "publish now". `accountIds`
// (same order) is required for the per-account caps, and lets YouTube pass
// posts to an account that uploads through Post Bridge.
export const verifyPublishCapacity = async (params: {
  provider: string;
  accessToken: string;
  scheduledAts: (Date | undefined)[];
  accountIds: string[];
  excludePublicationIds?: string[];
}): Promise<{
  results: PublishCapacityVerificationResult[];
  userDailyLimit?: number;
  accountDailyLimit?: number;
}> => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_API_URL}/integrations/providers/${params.provider}/verify-schedule`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${params.accessToken}`,
      },
      body: JSON.stringify({
        scheduled_ats: params.scheduledAts.map((date) =>
          (date ?? new Date()).toISOString()
        ),
        account_ids: params.accountIds,
        exclude_publication_ids:
          params.excludePublicationIds && params.excludePublicationIds.length > 0
            ? params.excludePublicationIds
            : undefined,
      }),
    }
  );
  if (!response.ok) throw new Error("Failed to verify the schedule");

  const data = await response.json();
  return {
    userDailyLimit: data.user_daily_limit,
    accountDailyLimit: data.account_daily_limit,
    results: (data.results as any[]).map((result) =>
      result.ok
        ? { ok: true }
        : {
            ok: false,
            reason: result.reason,
            suggestedAt: result.suggested_at
              ? new Date(result.suggested_at)
              : undefined,
          }
    ),
  };
};

// The verify call of each provider among `posts`, run together, leaving out
// `excludePublicationIds` (posts the save moves or deletes). Results are
// keyed by post; limits come back per provider. A provider whose verify fails
// has no results: its posts go unchecked, the others still are.
export const verifyCapacityByProvider = async <
  T extends { provider: string; accountId: string; scheduledAt?: Date },
>(accessToken: string, posts: T[], excludePublicationIds: string[]) => {
  const providers = [...new Set(posts.map((post) => post.provider))];
  const verifications = await Promise.all(
    providers.map(async (provider) => {
      const group = posts.filter((post) => post.provider === provider);
      const verification = await verifyPublishCapacity({
        provider,
        accessToken,
        scheduledAts: group.map((post) => post.scheduledAt),
        accountIds: group.map((post) => post.accountId),
        excludePublicationIds,
      }).catch((error) => {
        console.error(`Publish capacity verification failed (${provider})`, error);
        return null;
      });
      return { provider, group, verification };
    }),
  );
  const results = new Map<T, PublishCapacityVerificationResult>();
  const accountDailyLimits: Partial<Record<string, number>> = {};
  let userDailyLimit: number | undefined;
  for (const { provider, group, verification } of verifications) {
    if (!verification) continue;
    group.forEach((post, index) => results.set(post, verification.results[index]));
    if (verification.accountDailyLimit)
      accountDailyLimits[provider] = verification.accountDailyLimit;
    userDailyLimit ??= verification.userDailyLimit;
  }
  return { results, accountDailyLimits, userDailyLimit };
};

// Utility functions
export const serializePublication = (
  publication: Partial<PublicationModel>
): Database["public"]["Tables"]["publications"]["Update"] => {
  return {
    id: publication.id,
    created_at: publication.createdAt?.toISOString(),
    export_id: publication.exportId,
    status: publication.status,
    owner_id: publication.ownerId,
    published_url: publication.publishedUrl,
    provider: publication.provider,
    scheduled_at: publication.scheduledAt?.toISOString(),
    account_id: publication.accountId,
    post_info: camelToSnakeCase(publication.postInfo),
    project_id: publication.projectId,
    video_url: publication.videoUrl,
    error_code: publication.errorCode,
  };
};

export const deserializePublication = (
  publication: Database["public"]["Tables"]["publications"]["Row"]
): PublicationModel => {
  return {
    id: publication.id,
    createdAt: new Date(publication.created_at),
    exportId: publication.export_id,
    status: publication.status as PublicationModel["status"],
    ownerId: publication.owner_id,
    publishedUrl: publication.published_url,
    provider: publication.provider as PublicationModel["provider"],
    scheduledAt: new Date(publication.scheduled_at),
    accountId: publication.account_id,
    postInfo: snakeToCamelCase(publication.post_info),
    projectId: publication.project_id,
    autoFolderId: publication.auto_folder_id ?? null,
    videoUrl: publication.video_url,
    errorCode: publication.error_code,
  };
};
